import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { Button, Group, Stack } from '@repletoryreach/sdk-ui';
import { BreadcrumbTrail, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../i18n/I18nProvider';
import { HelpButton } from '../../app/guides/HelpButton';
import { ContactDrawer } from './components/ContactDrawer';
import './contacts.css';

/**
 * A contact on a page of its own (8 October 2026), at /contacts/:id: the same details as the drawer, with the
 * conversations, consent, history and removal beside them instead of behind tabs. A row in the list still opens
 * the drawer; "Open full page" in the drawer, or any link to /contacts/:id, comes here.
 */
export default function ContactPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const detail = useQuery({ queryKey: ['contact', id], queryFn: () => contacts.getContact(id), enabled: !!id });
  const c = detail.data?.data;
  const name = c?.name || c?.msisdn || c?.email || t('contacts.drawer.view');

  return (
    <Stack gap="lg">
      <BreadcrumbTrail
        items={[{ label: t('contacts.title'), href: '/contacts' }, { label: name }]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />
      <PageHeader
        title={name}
        description={t('contacts.page.description')}
        withDivider={false}
        actions={(
          <Group gap="xs">
            <HelpButton guide="audience" />
            <Button size="sm" variant="default" onClick={() => nav(`/contacts?contact=${encodeURIComponent(id)}`)}>{t('contacts.page.inList')}</Button>
          </Group>
        )}
      />
      <ContactDrawer
        contact={{ id }}
        layout="page"
        onClose={() => nav('/contacts')}
        onSaved={() => { void qc.invalidateQueries({ queryKey: ['contacts'] }); void qc.invalidateQueries({ queryKey: ['contact', id] }); }}
      />
    </Stack>
  );
}
